/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useState } from 'react';
import { Configure, InstantSearch, connectAutoComplete } from 'react-instantsearch-dom';

import algoliasearch from 'algoliasearch/lite';

import { SelectOption } from '@coursera/cds-core';

import type { ComboBoxItem } from 'bundles/employability-common/components/ComboBox';
import SelectWithCreateNewOption from 'bundles/employability-common/components/SelectWithCreateNewOption';
import { ALGOLIA_API_KEY } from 'bundles/search-common/constants';

import _t from 'i18n!nls/account-profile';

const E2E_SELECTORS = {
  INSTITUTION: 'profile-education-institution',
} as const;

type Hit = {
  accountId: string;
  institution: string;
  institutionOriginal: string;
  institutionSlug: string;
  objectID: string;
};

type PropsFromConnectAutoComplete = {
  refine: (query: string) => void;
  hits: Array<Hit>;
};

type DropdownProps = {
  value: string;
  onSelect: (value: string) => void;
  errorMessage?: string;
};

const styles = {
  selectOption: css`
    width: 100%;
  `,
};

const UniversitySelectDropdownWrapper = ({ value, onSelect, errorMessage }: DropdownProps) => {
  const searchClient = algoliasearch('LUA9B20G37', ALGOLIA_API_KEY);

  const UniversitySelectDropdown = ({ hits, refine }: PropsFromConnectAutoComplete) => {
    const [options, setOptions] = useState<Array<ComboBoxItem>>([]);

    const onSearch = (query: string) => refine(query);

    const formatCustomComboBoxItem = (hit: Hit) => {
      const { institution } = hit;
      return {
        key: institution,
        label: institution,
        onClick: (closePopover?: () => void) => {
          onSelect(institution);
          closePopover?.();
        },
        match: () => 1,
        content: () => (
          <SelectOption value={institution} css={styles.selectOption}>
            {institution}
          </SelectOption>
        ),
      };
    };

    useEffect(() => {
      setOptions(hits.map(formatCustomComboBoxItem));
    }, [hits]);

    return (
      <span className="fs-exclude">
        <SelectWithCreateNewOption
          data-e2e={E2E_SELECTORS.INSTITUTION}
          options={[]}
          customComboBoxItems={options}
          onSelect={onSelect}
          placeholder={value || _t('Select institution')}
          onSearchChange={onSearch}
          errorMessage={errorMessage}
        />
      </span>
    );
  };

  const UniversityCustomSelect = connectAutoComplete(UniversitySelectDropdown);

  return (
    // @ts-expect-error TS2769: No overload matches this call.
    <InstantSearch searchClient={searchClient} indexName="prod_enterprise_universities">
      <Configure hitsPerPage={10} />
      <UniversityCustomSelect />
    </InstantSearch>
  );
};

export default UniversitySelectDropdownWrapper;
